import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/faq/')({
  head: () => ({
    meta: [
      { title: 'FAQ / Unilytics' },
      { name: 'description', content: 'Frequently asked questions about Unilytics, our data sources, and the Unilytics Difficulty Index.' },
    ],
  }),
  component: RouteComponent,
})

const heroStyle = {
  background: 'var(--school-primary)',
  backgroundImage: `
    linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)
  `,
  backgroundSize: '40px 40px',
}

const FAQ_ITEMS = [
  {
    q: 'Is all the grade data accurate?',
    a: `All grade data on Unilytics is sourced directly from official university records obtained through public records requests. The data is 100% authentic — we do not create, estimate, or modify grade records.

However, university-provided data occasionally contains gaps or anomalies (for example, sections where all grades are recorded as 0). Where this occurs, we note the data limitation and exclude anomalous records from GPA calculations to protect accuracy.`,
  },
  {
    q: 'Where does Unilytics get its data?',
    a: `Our data comes from two sources: Freedom of Information Act (FOIA) requests submitted to public universities, and publicly available institutional research publications. Every dataset is verified against the official source before publication.

We do not use scraped data, student-submitted data, or any unverified third-party sources.`,
  },
  {
    q: 'What is the Unilytics Difficulty Index (UDI)?',
    a: `The UDI is Unilytics' proprietary course difficulty score. It combines 7 data signals — including withdrawal rate, grade distribution, average GPA, fail rate, class size, professor variance, and semester trends — into a single 0–10 score.

A UDI of 0–3.5 indicates low difficulty; 3.5–5.5 moderate; 5.5–7.5 high; 7.5–10 very high. UDI scores are normalized within each school and designed to be comparable across universities.`,
  },
  {
    q: 'When is data updated?',
    a: `We update grade data each semester as universities release their official records, typically 4–6 weeks after grades are posted. The data timestamp for each course shows the most recent semester included.`,
  },
  {
    q: 'Is Unilytics affiliated with any university?',
    a: `No. Unilytics is an independent platform. We are not affiliated with, endorsed by, or connected to any university. All data is obtained through public records requests and used in accordance with applicable law.`,
  },
  {
    q: "Why doesn't my course show up?",
    a: `Unilytics currently covers the University of Houston with data from Fall 2013 through the most recent semester. If your course is not listed, it may be a new course, a course with insufficient sections for reliable data, or a course not included in our current dataset.

We are continuously expanding our coverage.`,
  },
  {
    q: 'How do I request my university?',
    a: `Use the contact link on our About page or email us at hello@unilytics.io. We prioritize universities based on student demand and availability of public grade records.`,
  },
  {
    q: 'Is this website meant to harm professors?',
    a: `No. Unilytics publishes only aggregate, anonymized grade outcome data from official university records. We do not publish opinions, reviews, or personal information about instructors beyond what is contained in official grade records.

Our goal is to help students make informed decisions — not to target individuals.`,
  },
]

function RouteComponent() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div style={{ fontFamily: 'var(--font-family-sans)' }}>

      {/* ── Hero ── */}
      <section style={{ ...heroStyle, padding: '64px 0 56px', color: '#fff' }}>
        <div className="max-w-4xl mx-auto px-6">
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 14 }}>
            Help Center
          </p>
          <h1 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 14px', color: '#fff' }}>
            Frequently Asked Questions
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: 0 }}>
            Everything you need to know about Unilytics and how we work.
          </p>
        </div>
      </section>

      {/* ── FAQ Accordion ── */}
      <section style={{ background: '#fff', padding: '56px 0 80px' }}>
        <div className="max-w-3xl mx-auto px-6">
          <div>
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openIndex === i
              return (
                <div
                  key={i}
                  style={{ borderBottom: '1px solid #E2E8F0' }}
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '20px 0',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      gap: 16,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: isOpen ? 'var(--school-primary)' : '#0D1B2A',
                        lineHeight: 1.4,
                        transition: 'color 0.15s',
                      }}
                    >
                      {item.q}
                    </span>
                    <span
                      style={{
                        fontSize: 16,
                        color: isOpen ? 'var(--school-primary)' : '#94A3B8',
                        flexShrink: 0,
                        transition: 'transform 0.2s, color 0.15s',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        display: 'inline-block',
                      }}
                    >
                      ▾
                    </span>
                  </button>

                  {isOpen && (
                    <div style={{ paddingBottom: 20 }}>
                      {item.a.split('\n\n').map((para, pi) => (
                        <p
                          key={pi}
                          style={{
                            fontSize: 14,
                            color: '#475569',
                            lineHeight: 1.8,
                            margin: pi === 0 ? 0 : '12px 0 0',
                          }}
                        >
                          {para}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Bottom CTA */}
          <div
            style={{
              marginTop: 56,
              padding: '32px 28px',
              background: '#F8FAFC',
              borderRadius: 16,
              border: '1px solid rgba(13,27,42,0.06)',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: 15, fontWeight: 600, color: '#0D1B2A', margin: '0 0 6px' }}>
              Still have questions?
            </p>
            <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 20px' }}>
              We're happy to help.
            </p>
            <a
              href="mailto:hello@unilytics.io"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: 'var(--school-primary)',
                padding: '10px 24px',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              Contact Us →
            </a>
          </div>
        </div>
      </section>

    </div>
  )
}
